/**
 * 近 24h 验证摘要（P-02）。
 *
 * deny 不是错误：客户端拿着已撤销的授权来验证，服务端返回否定裁决是**正确行为**。
 * 因此 deny 用中性的 `--st-void`，只有 error（服务端故障 5xxx 段）才用 `--danger`。
 * 把这两者都染红，会让运营者对红色脱敏，真出故障时反而看不见。
 */
import { Icon } from '../../icons/Icon';
import { cssVar, ICON_SIZE } from '../../tokens';
import type { TokenName } from '../../tokens';
import type { IconName } from '../../icons/registry';
import type { StatsOverview } from '../../api/types';
import { formatCount } from '../../lib/format';

interface Cell {
  readonly label: string;
  readonly value: string;
  readonly token: TokenName;
  readonly icon: IconName;
  readonly hint: string;
}

export function ValidationSummary({ data }: { data: StatsOverview['validations_24h'] }) {
  const denyRate = data.total === 0 ? 0 : (data.deny / data.total) * 100;

  const cells: Cell[] = [
    {
      label: '验证请求',
      value: formatCount(data.total),
      token: 'fg',
      icon: 'activity',
      hint: `p95 延迟 ${formatCount(data.p95_latency_ms)} ms`,
    },
    {
      label: '肯定裁决',
      value: formatCount(data.allow),
      token: 'st-live',
      icon: 'circle-check',
      hint: '签名后返回，客户端可离线信任',
    },
    {
      label: '否定裁决',
      value: formatCount(data.deny),
      token: 'st-void',
      icon: 'ban',
      hint: `占比 ${denyRate.toFixed(2)}% · 已撤销 / 已过期 / 超设备数`,
    },
    {
      label: '服务端故障',
      value: formatCount(data.error),
      token: data.error > 0 ? 'danger' : 'muted',
      icon: data.error > 0 ? 'octagon-x' : 'shield-check',
      hint: '5xxx 段不签名，SDK 会回退到离线宽限期',
    },
  ];

  return (
    <section aria-labelledby="validation-heading">
      <h2 className="section-title" id="validation-heading">
        近 24 小时验证
      </h2>
      <div className="card-grid">
        {cells.map((cell) => (
          <div className="card" key={cell.label}>
            <div className="metric__label">
              <span style={{ color: cssVar(cell.token), display: 'inline-flex' }}>
                <Icon name={cell.icon} size={ICON_SIZE.inline} />
              </span>
              {cell.label}
            </div>
            <div className="metric__value">{cell.value}</div>
            <div className="metric__hint">{cell.hint}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
